/**
 * ForgotPasswordPage — Écran "Mot de passe oublié" public.
 *
 * Saisie de l'adresse → POST /api/auth/password-reset/request → message neutre.
 * La personne reçoit un courriel ; le bouton de ce courriel déclenche l'envoi d'un
 * mot de passe temporaire par SMS (30 min). Elle revient ici et s'en sert comme
 * mot de passe ; l'application lui fait ensuite en choisir un vrai.
 *
 * /!\ CETTE PAGE APPELAIT l'Edge Function `password-reset-request` par le client Supabase.
 * Le parcours a été repris par le BACKEND le 2026-09-21 (courriel par son SMTP,
 * SMS par SMS.to), côté mobile seulement : le web est resté sur l'Edge Function,
 * et personne ne l'a vu parce que tous les essais passaient par le téléphone. Sur
 * le projet servup4i, AUCUNE Edge Function n'est déployée — les trois rendent 404.
 * Le 23/09, l'administrateur du compte CAFOM a suivi la procédure à la lettre sans
 * jamais rien recevoir. La réponse du serveur étant NEUTRE par construction (elle
 * ne dit pas si le compte existe), rien ne trahissait la panne.
 */

import React, { useState } from 'react';
import { useViewport } from '../hooks/useViewport';

interface Props {
  onBackToLogin: () => void;
}

function ForgotPasswordPage({ onBackToLogin }: Props) {
  const { isPhone } = useViewport();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      console.log('[ForgotPassword] demande de réinitialisation pour', email);
      const reponse = await fetch('/api/auth/password-reset/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });

      if (!reponse.ok) {
        // Le serveur répond NEUTREMENT quand tout va bien (il ne révèle pas si le
        // compte existe) : un statut d'erreur est donc une vraie panne, et elle
        // doit se voir — c'est ce qui a manqué le 23/09/2026.
        console.error('[ForgotPassword] Le serveur a refusé :', reponse.status);
        setError('Impossible d\'envoyer la demande. Réessayez plus tard.');
        return;
      }

      setSuccess(true);
    } catch (err) {
      console.error('[ForgotPassword] Erreur:', err);
      setError(err instanceof Error ? err.message : 'Erreur de connexion au serveur.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#f1f5f9',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: isPhone ? 'calc(100vw - 24px)' : '460px',
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        padding: isPhone ? '24px' : '40px'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: success ? '#dcfce7' : '#dbeafe',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            {success ? (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            )}
          </div>
          <h1 style={{
            fontSize: '22px',
            fontWeight: '700',
            color: '#1e293b',
            margin: '0 0 8px'
          }}>
            {success ? 'Email envoyé' : 'Mot de passe oublié'}
          </h1>
          <p style={{
            color: '#64748b',
            fontSize: '14px',
            margin: 0,
            lineHeight: '1.5'
          }}>
            {success
              ? 'Si un compte existe pour cette adresse, vous recevrez un email avec les instructions de réinitialisation.'
              : 'Saisissez votre adresse email. Vous recevrez un lien de réinitialisation, puis un mot de passe temporaire par SMS pour vous reconnecter.'}
          </p>
        </div>

        {success ? (
          <>
            <div style={{
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px',
              fontSize: '13px',
              color: '#1e3a8a',
              lineHeight: '1.6'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '8px', color: '#1e40af' }}>
                Étapes suivantes&nbsp;:
              </div>
              <ol style={{ margin: 0, paddingLeft: '20px' }}>
                <li>Ouvrez l'email reçu et cliquez sur le lien de réinitialisation.</li>
                <li>Un mot de passe temporaire à 6 chiffres vous sera envoyé par SMS (valable 30 min).</li>
                <li>Revenez sur l'écran de connexion et saisissez votre email avec le mot de passe temporaire reçu.</li>
                <li>Vous serez ensuite invité à définir un nouveau mot de passe.</li>
              </ol>
            </div>

            <button
              onClick={onBackToLogin}
              type="button"
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.15s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
            >
              Retour à la connexion
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@exemple.com"
                required
                autoFocus
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '15px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.15s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>

            {error && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fca5a5',
                borderRadius: '8px',
                padding: '10px 12px',
                marginBottom: '16px',
                fontSize: '13px',
                color: '#991b1b',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#dc2626">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: loading ? '#93c5fd' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.15s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '12px'
              }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#2563eb'; }}
              onMouseLeave={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#3b82f6'; }}
            >
              {loading && (
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }}></div>
              )}
              <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
              {loading ? 'Envoi...' : 'Envoyer le lien de réinitialisation'}
            </button>

            <button
              onClick={onBackToLogin}
              type="button"
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: 'transparent',
                color: '#64748b',
                border: 'none',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              ← Retour à la connexion
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
